import {
  PaymentProcessorEnum,
  PrimaryButton,
  Tooltip,
  getPaymentProcessorTitle,
  toast,
} from '@systemeio/ui'
import { Integration } from 'modules/profile/payment-gateways/components/integration'
import { useConnectedPaymentIntegrations } from 'modules/profile/payment-gateways/hooks/use-connected-payment-integrations'
import { mollieDisconnect } from 'modules/profile/payment-gateways/integrations/mollie/api'
import MollieProfileModal from 'modules/profile/payment-gateways/integrations/mollie/components/mollie-profile-modal'
import {
  MolliePaymentIntegrationInterface,
  PaymentMollieProfileInterface,
} from 'modules/profile/payment-gateways/integrations/mollie/types'
import { getMollieHelpPageLink } from 'modules/profile/payment-gateways/integrations/mollie/utils'
import { isDesiredIntegration } from 'modules/profile/payment-gateways/utils/is-desired-integration'
import React, { FC, useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { BinIcon, MollieLogoIcon, WarningIcon } from '@systemeio/ui'

export const Mollie: FC = () => {
  const { user } = useUser()
  const { t } = useLocoTranslation()
  const { getConnectedPaymentIntegration, mutatePaymentIntegrations, removePaymentIntegration } =
    useConnectedPaymentIntegrations()

  const [deleteOpened, setDeleteOpened] = useState(false)

  const title = getPaymentProcessorTitle(PaymentProcessorEnum.Mollie)

  const mollieIntegration = getConnectedPaymentIntegration<MolliePaymentIntegrationInterface>({
    processor: PaymentProcessorEnum.Mollie,
  })

  const onMollieProfileChange = async (newData: Omit<PaymentMollieProfileInterface, 'status'>) => {
    await mutatePaymentIntegrations(data => {
      if (data) {
        return data.map(integration => {
          if (
            isDesiredIntegration<MolliePaymentIntegrationInterface>(
              integration,
              PaymentProcessorEnum.Mollie,
            )
          ) {
            return {
              ...integration,
              details: {
                ...integration.details,
                profileId: newData.id,
                profileName: newData.name,
              },
            }
          }
          return integration
        })
      }
    }, false)
  }

  const onDelete = async () => {
    await mollieDisconnect()
    await removePaymentIntegration<MolliePaymentIntegrationInterface>({
      processor: PaymentProcessorEnum.Mollie,
    })
    toast.success(
      t('dashboard.settings.payment-gateways.account_removed', {
        payment: title,
      }),
    )
  }

  return (
    <Integration
      logo={<MollieLogoIcon />}
      additionalInfo={
        <a
          className="primary-link"
          href={getMollieHelpPageLink(user?.dashboardLocale)}
          target="_blank"
          rel="noreferrer"
        >
          <TransLoco
            t={t}
            i18nKey="dashboard.settings.payment_gateways.help_article_label"
            values={{ integration: title }}
          />
        </a>
      }
    >
      {mollieIntegration ? (
        <div className={'flex w-full justify-between gap-3 overflow-hidden'}>
          <div className={'flex flex-col gap-1 truncate'}>
            {!mollieIntegration.readyToUse && (
              <p className={'flex gap-1'}>
                {t('dashboard.settings.payment-gateways.integration_in_progress')}
              </p>
            )}
            <p className={'flex items-center gap-1 text-base lg:text-lg'}>
              <span>{`${t('dashboard.settings.payment-gateways.organization_name')}:`}</span>
              <span className={'text-text-primary truncate'}>
                {mollieIntegration.details.organizationName}
              </span>
            </p>
            <p className={'flex items-center gap-1 text-base lg:text-lg'}>
              <span>{`${t('dashboard.settings.payment-gateways.organization_email')}:`}</span>
              <span className={'text-text-primary truncate'}>
                {mollieIntegration.details.organizationEmail}
              </span>
            </p>
            <div className={'flex items-center gap-1 text-base lg:text-lg'}>
              <span>{`${t('dashboard.settings.payment-gateways.mollie_profile')}:`}</span>
              <span className={'text-text-primary truncate'}>
                {mollieIntegration.details.profileName}
              </span>
              <MollieProfileModal
                onMollieProfileChange={onMollieProfileChange}
                mollieAccount={mollieIntegration.details}
              />
            </div>
            {!mollieIntegration.details.profileId && (
              <p className={'flex gap-1.5'}>
                <span>
                  {t('dashboard.settings.payment-gateways.mollie_choose_profile_warning-2')}
                </span>
                <WarningIcon />
              </p>
            )}
          </div>
          <Tooltip
            mode={'hover'}
            label={t('dashboard.settings.payment-gateways.remove_integration', {
              payment: title,
            })}
            wrapperClassName={'flex items-center'}
            className={'h-fit'}
          >
            <button className={'group outline-hidden'} onClick={() => setDeleteOpened(true)}>
              <BinIcon
                className={
                  'group-focus-visible:fill-danger hover:fill-danger main-transition-colors h-[16px] w-[16px]'
                }
              />
            </button>
          </Tooltip>
          <ConfirmationModal
            title={t('dashboard.settings.payment-gateways.remove_integration_description', {
              payment: title,
            })}
            confirmationText={t('dashboard.settings.payment-gateways.remove_payment_text.mollie')}
            opened={deleteOpened}
            onClose={() => setDeleteOpened(false)}
            onConfirm={onDelete}
          />
        </div>
      ) : (
        <PrimaryButton
          type={'button'}
          className={'font-bold'}
          onClick={() =>
            window.open(
              `/api/dashboard/customer/settings/connected-integrations/${PaymentProcessorEnum.Mollie}/auth`,
              '_self',
            )
          }
        >
          {t('dashboard.settings.payment_gateways.connect')}
        </PrimaryButton>
      )}
    </Integration>
  )
}
